import { useEffect, useMemo, useState } from "react";
import Sheet from "../Sheet.jsx";
import { useQuery, useDataEpoch } from "../../lib/dataClient.js";
import { NOTE_CLASS } from "../../lib/ui.js";
import { loadClusterTree } from "../../lib/clusterTree.js";
import { rollupActivityMap, treeDepth } from "../../lib/clusterIcicle.js";
import HierarchyMap, { MapSkeleton } from "./HierarchyMap.jsx";

// The status line, as a pure function of load state — so the loading / error /
// loaded branches are testable without mounting (the error branch is only
// reachable after an async reject, which a server render won't run).
export function taxonomyStatus({ tree, loadError, clickHint }) {
  if (tree == null) return loadError ? "Couldn't load the taxonomy. Reload to try again." : "Loading taxonomy…";
  const depth = treeDepth(tree);
  return `${tree.byId.size} topics across ${depth} level${depth === 1 ? "" : "s"} · heat = this scope's activity · ${clickHint}`;
}

// ScopeTaxonomyMap — "where you are" as a flame-graph atlas of the whole trace
// lake. The cluster taxonomy is the stable map of ALL activity (geometry ∝
// structure, so it's the same atlas at every scope); the CURRENT scope projects
// onto it as HEAT — brighter bands are where this scope's traffic actually
// lives. That's your position no matter what you scoped by:
//
//   • a cluster scope lights its own subtree and pins the node (crosshair);
//   • an endpoint / agent / model scope has no single node — its heat FOOTPRINT
//     across the taxonomy IS its position ("this machine works here, not there").
//
// This is the generalization of the cluster Detail map: same query
// (getClusterLeafActivity drops cluster steps and counts the scope's prompts per
// leaf and parent subtree), same icicle, driven by whatever scope is committed. Clicking a band
// navigates (drill into the region); the caller decides what that means.

export default function ScopeTaxonomyMap({
  scopePath = [],
  range,
  selectedClusterId = null,
  onNavigateCluster,
  eyebrow = "Position",
  title = "Where you are",
  clickHint = "click a region to drill in",
}) {
  // Structure — the cached, generation-pin-aware tree (same atlas everywhere).
  const [tree, setTree] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const epoch = useDataEpoch();
  useEffect(() => {
    let on = true;
    loadClusterTree()
      .then((t) => { if (on) setTree(t); })
      .catch((err) => { if (on) { console.error("[ScopeTaxonomyMap] loadClusterTree", err); setLoadError(err); } });
    return () => { on = false; };
  }, [epoch]);

  // Heat uses distinct prompt counts for each leaf and parent subtree.
  const { data: leafRows, loading } = useQuery("getClusterLeafActivity", { scopePath, range, includeParents: true });
  const activity = useMemo(() => rollupActivityMap(tree, leafRows || []), [tree, leafRows]);

  const flatShape = tree != null && !tree.isHierShape;
  const status = taxonomyStatus({ tree, loadError, clickHint });

  return (
    <Sheet eyebrow={eyebrow} title={title} diagram tight>
      <span className={`${NOTE_CLASS} block pb-[2px]`}>{status}</span>
      {flatShape ? (
        <span className={`${NOTE_CLASS} block pb-[2px]`}>
          This generation has no topic hierarchy. The map shows a ranked strip.
        </span>
      ) : null}
      {/* Activity-surface chrome: diagram-dot paper on the Sheet, the map on a
          solid plot-bg panel. */}
      <div className="mt-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] p-[8px]">
        {tree == null && loadError ? (
          <span className={`${NOTE_CLASS} block py-[24px] text-center`}>The topic taxonomy failed to load.</span>
        ) : tree == null || (loading && !(leafRows || []).length) ? (
          <MapSkeleton />
        ) : (
          <HierarchyMap
            tree={tree}
            activity={activity}
            selectedId={selectedClusterId || ""}
            onSelect={onNavigateCluster}
            ariaLabel="Trace-lake taxonomy map. Select a region to navigate."
            emptyLabel="No topics in this generation yet."
            unit="prompt"
          />
        )}
      </div>
    </Sheet>
  );
}
